import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatStepper, MatStep, MatStepLabel } from '@angular/material/stepper';
import { AddDeviceNoteDetailsComponent } from './add-device-note-details/add-device-note-details.component';
import { ActivatedRoute } from '@angular/router';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { AppVisitNote } from 'src/app/models/data/appVisit';
import { ApiService } from 'src/app/services/api.services';
import { Device } from 'src/app/models/data/device';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { MatButton } from '@angular/material/button';
import { AddDeviceNoteConfirmationComponent } from './add-device-note-confirmation/add-device-note-confirmation.component';

enum StepperPage {
	details = 0,
	confirmation = 1,
}

@Component({
	selector: 'app-add-device-note',
	templateUrl: './add-device-note-main-tab.component.html',
	styleUrls: [
		'./add-device-note-main-tab.component.scss',
		'../../ui/common/stepper.scss',
		'../../ui/common/main.scss',
	],
	imports: [
		AppNavigationHeaderComponent,
		MatButton,
		MatStepper,
		MatStep,
		MatStepLabel,
		AddDeviceNoteDetailsComponent,
		AddDeviceNoteConfirmationComponent,
	],
})
export class AddDeviceNoteMainTabComponent implements OnInit {
	stepper!: MatStepper;
	isEditable = false;
	deviceId: string = '';
	device!: Device;
	appVisitNote: AppVisitNote = emptyAppVisitNote();

	@Output() addAppVisitNoteStatusEvents =
		new EventEmitter<AddAppVisitNoteStatusEvent>();

	@ViewChild(AddDeviceNoteDetailsComponent)
	addDeviceNoteDetails?: AddDeviceNoteDetailsComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly navHistory: NavigationHistoryService
	) {
		this.deviceId = this.route.snapshot.queryParamMap.get('deviceId') as string;
		this.stepper = this.stepperRef as unknown as MatStepper;
	}

	async ngOnInit(): Promise<void> {
		if (this.deviceId) {
			this.device = await this.apiService.getDeviceById(this.deviceId);
			this.appVisitNote.deviceId = this.deviceId;
		}
	}

	movePrevious(stepper: MatStepper): void {
		switch (stepper.selectedIndex) {
			case StepperPage.details:
				this.navHistory.navigateBack();
				break;
			case StepperPage.confirmation:
				stepper.previous();
				break;
		}
	}

	moveNext(stepper: MatStepper): void {
		switch (stepper.selectedIndex) {
			case StepperPage.details:
				this.addDeviceNote(stepper);
				break;
		}
	}

	isValid(): boolean {
		return this.addDeviceNoteDetails?.isValid ?? false;
	}

	async addDeviceNote(stepper: MatStepper): Promise<void> {
		try {
			if (this.appVisitNote) {
				// if note wasn't passed in set it to ''
				this.appVisitNote.note = this.appVisitNote.note
					? this.appVisitNote.note
					: '';
				this.appVisitNote.deviceId = this.deviceId;

				const appVisitNoteId = await this.apiService.addNote(
					this.deviceId,
					this.appVisitNote,
					this.addDeviceNoteDetails?.selectedFileDetails
				);
				if (appVisitNoteId) {
					this.addAppVisitNoteStatusEvents.emit({
						status: AddAppVisitNoteStatus.success,
						data: {
							note: this.appVisitNote.note,
							photo: this.addDeviceNoteDetails?.selectedFileDetails,
						},
					});

					stepper.next(); // go to the confirmation page
				} else {
					this.addAppVisitNoteStatusEvents.emit({
						status: AddAppVisitNoteStatus.failure,
						data: { note: this.appVisitNote.note },
					});
					stepper.next(); // go to the confirmation page
				}
			}
		} catch (err) {
			this.addAppVisitNoteStatusEvents.emit({
				status: AddAppVisitNoteStatus.failure,
				data: { note: this.appVisitNote.note },
			});
			stepper.next(); // go to the confirmation page
		}
	}
}

export const emptyAppVisitNote = (): AppVisitNote => ({
	note: '',
});

export enum AddAppVisitNoteStatus {
	loading,
	success,
	failure,
}

export interface AddAppVisitNoteStatusEvent {
	status: AddAppVisitNoteStatus;
	data?: {
		note: string;
		photo?: {
			file: File;
			previewURL: string | ArrayBuffer | null;
			caption: string;
		}[];
	};
}
